import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';

const Cart = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const cart = await response.json();
        
        // Fetch product details for each item
        const itemsWithDetails = await Promise.all(
          cart.items.map(async (item) => {
            const productResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${item.productId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            
            if (productResponse.ok) {
              const product = await productResponse.json();
              return { ...product, quantity: item.quantity };
            }
            return item;
          })
        );
        
        setCartItems(itemsWithDetails);
      } else {
        throw new Error('Failed to fetch cart');
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast({
        title: "Error",
        description: "Failed to load cart. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    try {
      const token = localStorage.getItem('dcTech_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: productId,
          quantity: newQuantity - cartItems.find(item => item.id === productId).quantity
        }),
      });

      // Update local state
      setCartItems(items =>
        items.map(item =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast({
        title: "Error",
        description: "Failed to update quantity. Please try again.",
        variant: "destructive",
      });
    }
  };

  const removeFromCart = (productId) => {
    setCartItems(items => items.filter(item => item.id !== productId));
    // Also update localStorage for immediate UI feedback
    const localCart = JSON.parse(localStorage.getItem('dcTech_cart') || '[]');
    const updatedCart = localCart.filter(item => item.id !== productId);
    localStorage.setItem('dcTech_cart', JSON.stringify(updatedCart));
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2);
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast({
        title: "Empty cart",
        description: "Please add items to your cart before checking out.",
        variant: "destructive",
      });
      return;
    }

    setProcessingPayment(true);

    try {
      const token = localStorage.getItem('dcTech_token');
      
      // First create the order
      const orderData = {
        type: 'product',
        items: cartItems.map(item => ({
          productId: item.id,
          quantity: item.quantity,
          price: item.price,
          name: item.name
        })),
        total: parseFloat(calculateTotal())
      };

      const orderResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!orderResponse.ok) {
        throw new Error('Failed to create order');
      }

      const order = await orderResponse.json();

      // Create payment session
      const paymentResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/create-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderType: 'product',
          orderId: order.id,
          hostUrl: window.location.origin
        }),
      });

      if (!paymentResponse.ok) {
        throw new Error('Failed to create payment session');
      }

      const paymentData = await paymentResponse.json();

      // Clear cart locally before redirecting
      setCartItems([]);
      localStorage.removeItem('dcTech_cart');

      // Redirect to Stripe Checkout
      window.location.href = paymentData.url;

    } catch (error) {
      console.error('Error during checkout:', error);
      toast({
        title: "Checkout failed",
        description: "Failed to initiate payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading cart...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="text-3xl">⚡</div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-white">D.C. Technology Corp.</span>
                <span className="text-[10px] text-white/70">ECRA/ESA #7018534</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Dashboard
                </Button>
              </Link>
              
              <Link to="/profile">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Profile
                </Button>
              </Link>
              
              <Button 
                onClick={logout}
                variant="outline" 
                className="border-red-400/50 text-red-400 bg-red-500/20"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl font-bold text-white mb-8">Shopping Cart</h1>

        {cartItems.length === 0 ? (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-8 text-center">
              <div className="text-6xl mb-6">🛒</div>
              <h2 className="text-2xl font-bold text-white mb-4">Your cart is empty</h2>
              <p className="text-white/70 mb-6">Add some equipment to get started!</p>
              <Link to="/dashboard">
                <Button className="bg-gradient-to-r from-blue-500 to-purple-500   text-white">
                  Continue Shopping
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Cart Items */}
            <div className="space-y-4">
              {cartItems.map(item => (
                <Card key={item.id} className="bg-white/10 backdrop-blur-lg border-white/20">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-6">
                      <img 
                        src={item.image || '/api/placeholder/100/100'} 
                        alt={item.name}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      
                      <div className="flex-1">
                        <h3 className="text-white font-semibold text-lg mb-2">{item.name}</h3>
                        <p className="text-white/70 mb-2">{item.category}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-green-400">${item.price}</span>
                          {item.originalPrice && (
                            <span className="text-white/60 line-through">${item.originalPrice}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline"
                            size="sm"
                            className="border-white/50 text-white bg-white/10"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          >
                            -
                          </Button>
                          <span className="text-white font-semibold px-4">{item.quantity}</span>
                          <Button 
                            variant="outline"
                            size="sm"
                            className="border-white/50 text-white bg-white/10"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          >
                            +
                          </Button>
                        </div>
                        
                        <div className="text-right min-w-[100px]">
                          <div className="text-white font-bold text-lg">
                            ${(item.price * item.quantity).toFixed(2)}
                          </div>
                        </div>
                        
                        <Button 
                          variant="outline"
                          size="sm"
                          className="border-red-400/50 text-red-400 bg-red-500/20"
                          onClick={() => removeFromCart(item.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Cart Summary */}
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-2xl">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center text-lg">
                  <span className="text-white/70">Subtotal:</span>
                  <span className="text-white font-semibold">${calculateTotal()}</span>
                </div>
                <div className="flex justify-between items-center text-lg">
                  <span className="text-white/70">Shipping:</span>
                  <span className="text-green-400">FREE</span>
                </div>
                <hr className="border-white/20" />
                <div className="flex justify-between items-center text-2xl">
                  <span className="text-white font-bold">Total:</span>
                  <span className="text-green-400 font-bold">${calculateTotal()}</span>
                </div>
                
                <div className="flex gap-4 pt-4">
                  <Link to="/dashboard" className="flex-1">
                    <Button variant="outline" className="w-full border-white/50 text-white bg-white/10">
                      Continue Shopping
                    </Button>
                  </Link>
                  <Button 
                    onClick={handleCheckout}
                    disabled={processingPayment}
                    className="flex-1 bg-gradient-to-r from-green-500 to-blue-500   text-white"
                  >
                    {processingPayment ? "Processing..." : "Secure Checkout"}
                  </Button>
                </div>

                <div className="text-center text-sm text-white/60 mt-4">
                  🔒 Secure payment powered by Stripe
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;